import React, { useState, useMemo } from 'react';
import { fetchAllDeviceEvents } from '_services/coverage-service';

// ── helpers 

function toDateInputValue(date) {
    return date.toISOString().slice(0, 10);
}

const TYPE_BADGE = {
    location_fixed:           { bg: '#0d6efd', label: 'Location Fixed' },
    location_fixed_gps_limit: { bg: '#0d6efd', label: 'Location Fixed (GPS Limit)' },
    heartbeat:        { bg: '#198754', label: 'Heartbeat' },
    location_failure: { bg: '#f0a030', label: 'Location Failure' },
    event:            { bg: '#6f42c1', label: 'Event' },
    shutdown:         { bg: '#6c757d', label: 'Shutdown' },
};

function TypeBadge({ type }) {
    const cfg = TYPE_BADGE[type] ?? { bg: '#adb5bd', label: type ?? '—' };
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            color: '#fff',
            background: cfg.bg,
            whiteSpace: 'nowrap',
        }}>
            {cfg.label}
        </span>
    );
}

function getDetails(evt) {
    const d = evt.decoded ?? {};
    switch (evt.type) {
        case 'location_fixed_gps_limit':
        case 'location_fixed': {
            if (d.lat != null && d.lng != null) return `GPS — ${d.lat}, ${d.lng}`;
            if (d.ble?.length) return `BLE - ${d.ble[0]?.mac ?? '—'}`;
            return d.positioningType ?? '-';
        }
        case 'heartbeat':
            return [
                d.header?.temperatureC != null ? `${d.header.temperatureC} °C` : null,
                d.firmwareVersion ? `FW ${d.firmwareVersion}` : null,
                d.header?.operationMode ?? null,
            ].filter(Boolean).join(' · ') || '-';
        case 'location_failure':
            return d.positioningFailureReason ?? d.failed ?? '-';
        case 'event':
            return d.eventType ?? '-';
        case 'shutdown':
            return d.rebootReason ?? '-';
        default:
            return '-';
    }
}

function getBestGateway(foundGateways) {
    if (!foundGateways?.length) return null;
    return [...foundGateways].sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999))[0];
}

// Recursively convert Firestore Timestamps to readable strings for JSON display
function sanitiseForJson(obj) {
    if (obj == null) return obj;
    if (typeof obj.toDate === 'function') return obj.toDate().toLocaleString();
    if (obj?.seconds != null && obj?.nanoseconds != null) return new Date(obj.seconds * 1000).toLocaleString();
    if (Array.isArray(obj)) return obj.map(sanitiseForJson);
    if (typeof obj === 'object') {
        return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, sanitiseForJson(v)]));
    }
    return obj;
}

export default function AssetTrackingInspect({ devices }) {

    const [search, setSearch] = useState('');
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 28);
        return toDateInputValue(d);
    });
    const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    const filteredDeviceList = useMemo(() =>
        (devices ?? []).filter(d =>
            !search || d.meta?.deviceName?.toLowerCase().includes(search.toLowerCase())
        ),
        [devices, search]
    );

    const typeCounts = useMemo(() => {
        const counts = {};
        for (const evt of events) {
            counts[evt.type] = (counts[evt.type] ?? 0) + 1;
        }
        return counts;
    }, [events]);

    const stats = useMemo(() => {
        if (!events.length) return null;
        const positioning = (typeCounts['location_fixed'] ?? 0) + (typeCounts['location_fixed_gps_limit'] ?? 0) + (typeCounts['location_failure'] ?? 0);
        const days = Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000));
        return {
            total:       events.length,
            positioning,
            events_:     typeCounts['event']       ?? 0,
            heartbeats:  typeCounts['heartbeat']   ?? 0,
            failures:    typeCounts['location_failure'] ?? 0,
            avgPerDay:   (events.length / days).toFixed(1),
            days,
            capped:      false,
        };
    }, [events, typeCounts, startDate, endDate]);

    async function loadEvents() {
        if (!selectedDevice) return;
        setLoading(true);
        setError(null);
        setEvents([]);
        setExpandedRow(null);
        try {
            const tenantId = selectedDevice.meta?.tenantId;
            const appName = selectedDevice.meta?.applicationName;
            const devEui = selectedDevice.id;
            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T23:59:59');
            const data = await fetchAllDeviceEvents(tenantId, appName, devEui, start, end);
            setEvents(data);
            if (data.length === 0) setError('No events found for this device in the selected date range.');
        } catch (e) {
            console.error('Inspect load error:', e);
            setError('Failed to load events. ' + (e?.message ?? ''));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ display: 'flex', height: '89vh', gap: 2 }}>

            {/* ── Left panel ── */}
            <div style={{
                width: 250, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10,
                background: '#fff', border: '1px solid #dee2e6', borderRadius: 8,
                padding: 12, overflowY: 'auto', flexShrink: 0,
            }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>Select Tracker</div>

                <input
                    type="text"
                    placeholder="Search trackers..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 12, outline: 'none' }}
                />

                <div style={{ overflowY: 'auto', maxHeight: 180 }}>
                    {filteredDeviceList.map(device => (
                        <div
                            key={device.id}
                            onClick={() => { setSelectedDevice(device); setEvents([]); setError(null); setExpandedRow(null); }}
                            style={{
                                padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                                background: selectedDevice?.id === device.id ? '#e7f1ff' : 'transparent',
                                fontWeight: selectedDevice?.id === device.id ? 600 : 400,
                                borderLeft: selectedDevice?.id === device.id ? '3px solid #0d6efd' : '3px solid transparent',
                                marginBottom: 2, userSelect: 'none',
                            }}
                        >
                            {device.meta?.deviceName || device.id}
                        </div>
                    ))}
                    {filteredDeviceList.length === 0 && (
                        <div style={{ fontSize: 11, color: '#adb5bd', padding: 8 }}>No trackers found</div>
                    )}
                </div>

                <hr style={{ margin: '2px 0' }} />

                <div style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>Date Range</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, color: '#6c757d', marginBottom: 0 }}>From</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        style={{ fontSize: 12, border: '1px solid #dee2e6', borderRadius: 4, padding: '3px 6px' }}
                    />
                    <label style={{ fontSize: 11, color: '#6c757d', marginBottom: 0 }}>To</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        style={{ fontSize: 12, border: '1px solid #dee2e6', borderRadius: 4, padding: '3px 6px' }}
                    />
                </div>

                <button
                    onClick={loadEvents}
                    disabled={!selectedDevice || loading}
                    style={{
                        padding: '7px 0',
                        background: (!selectedDevice || loading) ? '#dee2e6' : '#0d6efd',
                        color: (!selectedDevice || loading) ? '#6c757d' : '#fff',
                        border: 'none', borderRadius: 4, fontWeight: 600,
                        fontSize: 13, cursor: (!selectedDevice || loading) ? 'not-allowed' : 'pointer',
                        marginTop: 4,
                    }}
                >
                    {loading ? 'Loading…' : 'Load Events'}
                </button>

                {/* summary counts */}
                {events.length > 0 && (
                    <div style={{
                        fontSize: 11, color: '#495057', borderTop: '1px solid #dee2e6',
                        paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4,
                    }}>
                        <div><b>Total:</b> {events.length} uplinks</div>
                        {Object.entries(typeCounts).map(([type, count]) => (
                            <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <TypeBadge type={type} />
                                <span style={{ fontWeight: 600 }}>{count}</span>
                            </div>
                        ))}
                    </div>
                )}

                {error && (
                    <div style={{ fontSize: 11, color: '#856404', background: '#fff3cd', borderRadius: 4, padding: '6px 8px', lineHeight: 1.4 }}>
                        {error}
                    </div>
                )}
            </div>

            {/* ── Right panel ── */}
            <div style={{
                flex: 1, background: '#fff', border: '1px solid #dee2e6',
                borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column',
            }}>

                {/* Empty state */}
                {!selectedDevice && (
                    <div style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#6c757d', flexDirection: 'column', gap: 8,
                    }}>
                        <div style={{ fontSize: 36 }}>🔍</div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>Select a tracker to inspect uplinks</div>
                        <div style={{ fontSize: 12 }}>Pick a date range and click Load Events</div>
                    </div>
                )}

                {/* Loading state */}
                {selectedDevice && loading && (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>
                        Loading events…
                    </div>
                )}

                {/* Stats bar */}
                {stats && (
                    <div style={{ flexShrink: 0, padding: '10px 16px', borderBottom: '1px solid #dee2e6', background: '#f8f9fa', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total uplinks',  value: stats.total,       color: '#0d6efd', warn: stats.capped },
                            { label: 'Positioning',    value: stats.positioning,  color: '#0d6efd' },
                            { label: 'Fix failures',   value: stats.failures,     color: '#f0a030' },
                            { label: 'Events',         value: stats.events_,      color: '#6f42c1' },
                            { label: 'Heartbeats',     value: stats.heartbeats,   color: '#198754' },
                            { label: `Avg / day`,      value: stats.avgPerDay,    color: '#495057', sub: `over ${stats.days}d` },
                        ].map(({ label, value, color, warn, sub }) => (
                            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', border: `1px solid ${warn ? '#ffc107' : '#dee2e6'}`, borderRadius: 8, padding: '6px 14px', minWidth: 90 }}>
                                <span style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.2 }}>{value}{warn ? ' ⚠️' : ''}</span>
                                <span style={{ fontSize: 10, color: '#6c757d', marginTop: 1 }}>{label}</span>
                                {sub && <span style={{ fontSize: 9, color: '#adb5bd' }}>{sub}</span>}
                            </div>
                        ))}
                        {stats.capped && (
                            <div style={{ fontSize: 11, color: '#856404', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '5px 10px', marginLeft: 4 }}>
                                ⚠️ Result capped at 2000 events — actual count may be higher. Narrow the date range for accurate totals.
                            </div>
                        )}
                    </div>
                )}

                {/* Table */}
                {selectedDevice && !loading && events.length > 0 && (
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                                    {['Time', 'Type', 'fPort', 'RSSI', 'SNR', 'Positioning', 'Details'].map(col => (
                                        <th key={col} style={{
                                            padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                                            color: '#495057', borderBottom: '2px solid #dee2e6',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((evt, i) => {
                                    const gw = getBestGateway(evt.foundGateways);
                                    const ts = (evt.at?.toDate ? evt.at.toDate() : new Date((evt.at?.seconds ?? 0) * 1000)).toLocaleString();
                                    const details = getDetails(evt);
                                    const isExpanded = expandedRow === i;

                                    return (
                                        <React.Fragment key={evt.id ?? i}>
                                            <tr
                                                onClick={() => setExpandedRow(isExpanded ? null : i)}
                                                style={{
                                                    cursor: 'pointer',
                                                    background: isExpanded ? '#e7f1ff' : (i % 2 === 0 ? '#fff' : '#f8f9fa'),
                                                    borderBottom: '1px solid #dee2e6',
                                                    transition: 'background 0.1s',
                                                }}
                                                onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f0f4ff'; }}
                                                onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8f9fa'; }}
                                            >
                                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', color: '#495057' }}>
                                                    {ts}
                                                </td>
                                                <td style={{ padding: '7px 12px' }}>
                                                    <TypeBadge type={evt.type} />
                                                </td>
                                                <td style={{ padding: '7px 12px', color: '#6c757d' }}>
                                                    {evt.fPort ?? '—'}
                                                </td>
                                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', color: gw?.rssi != null ? '#212529' : '#adb5bd' }}>
                                                    {gw?.rssi != null ? `${gw.rssi} dBm` : '—'}
                                                </td>
                                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', color: gw?.snr != null ? '#212529' : '#adb5bd' }}>
                                                    {gw?.snr != null ? `${gw.snr} dB` : '—'}
                                                </td>
                                                <td style={{ padding: '7px 12px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                                    {evt.decoded?.positioningType ?? '—'}
                                                </td>
                                                <td style={{ padding: '7px 12px', color: '#495057', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {details}
                                                </td>
                                            </tr>

                                            {/* Expanded JSON row */}
                                            {isExpanded && (
                                                <tr style={{ background: '#f0f4ff' }}>
                                                    <td colSpan={7} style={{ padding: '0 12px 12px 12px' }}>
                                                        <div style={{
                                                            marginTop: 6,
                                                            borderRadius: 6,
                                                            border: '1px solid #dee2e6',
                                                            background: '#1e1e2e',
                                                            padding: '12px 16px',
                                                            overflowX: 'auto',
                                                        }}>
                                                            <pre style={{
                                                                margin: 0,
                                                                fontSize: 11,
                                                                lineHeight: 1.6,
                                                                color: '#cdd6f4',
                                                                fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                                                            }}>
                                                                {JSON.stringify(sanitiseForJson(evt), null, 2)}
                                                            </pre>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* No events after load */}
                {selectedDevice && !loading && events.length === 0 && !error && (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 24 }}>📭</div>
                        <div style={{ fontSize: 13 }}>No events yet — click Load Events</div>
                    </div>
                )}
            </div>
        </div>
    );
}
